import { type ReactNode, type SVGProps, useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../lib/useAuth";
import { adminApi, predefinedItemsApi, shopProfileApi, withBase } from "../lib/api";
import { useConfirmGate } from "../lib/useConfirm";
import { useShopType } from "../lib/theme";
import { DevEnvironmentBanner, ModernConfirmDialog, ModernFooter, modernButtonClassName } from "./ui-modern";
import { LegalAcceptanceGate } from "./LegalConsent";

/**
 * The MODERN theme's app shell — see docs/FRONTEND_BACKLOG.md Round 59 and
 * `Bay2Bill Redesign.dc.html` lines ~41-113 (sidebar + header) and
 * ~847-860 (bottom tab bar). Sibling to `./AppShell.tsx` (the classic
 * shell, left entirely untouched) — NOT a refactor of it.
 *
 * Layout: a dark-navy sidebar at `md`+ widths (icon-only rail at `md`,
 * full width with labels at `lg`+), replaced by a bottom tab bar below
 * `md`. A top header (search + "New job") is always shown — unlike the
 * mockup, where it's hidden on Login, Login here is never wrapped by this
 * shell at all (LoginPage.tsx/LoginPageModern.tsx are separate, un-shelled
 * routes), so that condition never applies.
 *
 * Data-fetching and gating logic (shop logo/name visibility, owner-only
 * Shop Settings, platform-admin-only Admin Panel, logout confirmation) is
 * mirrored EXACTLY from AppShell.tsx, not just its markup — see that
 * file's own comments for why each condition exists.
 */

/** Icons duplicated (not imported) from AppShell.tsx's own set — that file
 * is left entirely untouched by this round, so nothing in it is exported
 * for reuse. Same SVG paths, 1.7px stroke (matching the mockup) instead of
 * classic's 2px. */
function iconProps(props: SVGProps<SVGSVGElement>) {
  return {
    width: 19,
    height: 19,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    ...props,
  };
}

function HomeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconProps(props)}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
    </svg>
  );
}

function WorkOrderIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconProps(props)}>
      <path d="M9 4h6a1 1 0 0 1 1 1v1H8V5a1 1 0 0 1 1-1Z" />
      <rect x="4" y="6" width="16" height="15" rx="2" />
      <path d="M8 12h8M8 16h5" />
    </svg>
  );
}

function CustomersIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconProps(props)}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M15.5 14a5 5 0 0 1 5.5 5.5" />
    </svg>
  );
}

function InvoicesIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconProps(props)}>
      <path d="M6 3h9l3 3v15H6z" />
      <path d="M9 9h6M9 13h6M9 17h4" />
    </svg>
  );
}

function TechniciansIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconProps(props)}>
      <path d="M14.5 3.5a3.5 3.5 0 0 0-4.6 4.6L4 14v3l3 3h3l6.1-6.1a3.5 3.5 0 0 0 4.4-4.4l-2.6 2.6-2-2 2.6-2.6Z" />
    </svg>
  );
}

/** A clipboard with a check — the state Inspections log. */
function InspectionsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconProps(props)}>
      <path d="M9 4h6v3H9z" />
      <path d="M7 5.5H5v15h14v-15h-2" />
      <path d="m8.5 14 2.5 2.5 4.5-5" />
    </svg>
  );
}

/** Matches the mockup's own Payments nav icon exactly (a bill with a coin
 * on it) — see `Bay2Bill Redesign.dc.html` lines ~75-78. */
function PaymentsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconProps(props)}>
      <path d="M3 7h18v10H3z" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}

/** New — for the header's search field (the mockup's own header has a
 * dedicated search icon that AppShell.tsx's icon set has no equivalent
 * for, since the classic header uses a plain text "Search" button
 * instead). */
function SearchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconProps({ width: 17, height: 17, strokeWidth: 1.8, ...props })}>
      <circle cx="11" cy="11" r="7" />
      <path d="m16.5 16.5 4 4" />
    </svg>
  );
}

/** New — header's content-zoom controls (a magnifying glass with a
 * +/- to match SearchIcon's visual language). */
function ZoomOutIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconProps({ width: 18, height: 18, strokeWidth: 1.8, ...props })}>
      <circle cx="11" cy="11" r="7" />
      <path d="m16.5 16.5 4 4" />
      <path d="M8 11h6" />
    </svg>
  );
}

function ZoomInIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconProps({ width: 18, height: 18, strokeWidth: 1.8, ...props })}>
      <circle cx="11" cy="11" r="7" />
      <path d="m16.5 16.5 4 4" />
      <path d="M11 8v6M8 11h6" />
    </svg>
  );
}

/** Content-only zoom (the main `<main>` panel, not the sidebar/header) —
 * persisted so it survives navigation/reload. `zoom` (not `font-size` or a
 * `transform: scale`) is used on `<main>` because most of this theme's text
 * is sized with explicit px Tailwind classes, which wouldn't inherit a
 * parent `font-size` change, and `zoom` re-flows layout (no clipping/
 * overlap the way `transform: scale` would need extra width math for). */
const CONTENT_ZOOM_STORAGE_KEY = "b2b-content-zoom";
const CONTENT_ZOOM_MIN = 80;
const CONTENT_ZOOM_MAX = 150;
const CONTENT_ZOOM_STEP = 10;

function readStoredContentZoom(): number {
  const stored = Number(localStorage.getItem(CONTENT_ZOOM_STORAGE_KEY));
  return stored >= CONTENT_ZOOM_MIN && stored <= CONTENT_ZOOM_MAX ? stored : 100;
}

const NAV_ITEMS = [
  { to: "/", label: "Home", end: true, Icon: HomeIcon },
  { to: "/customers", label: "Customers", Icon: CustomersIcon },
  { to: "/work-orders", label: "Work Orders", Icon: WorkOrderIcon },
  { to: "/invoices", label: "Invoices", Icon: InvoicesIcon },
  { to: "/technicians", label: "Technicians", Icon: TechniciansIcon },
];

/** Same 5 items as NAV_ITEMS (owner-only Payments is appended separately
 * where this is rendered, same role gate the sidebar uses) — the mockup's
 * own bottom-tab set only had 4 (Today/Jobs/Customers/Invoices), leaving
 * out Technicians/Payments/Settings entirely, but the owner asked for
 * every sidebar item to be reachable at phone width too, not just a
 * subset (see docs/FRONTEND_BACKLOG.md). Rendered icon-only (no text
 * label) below, unlike the mockup's own labeled tabs — 7 items with
 * labels doesn't fit a phone-width row without wrapping/truncating;
 * icon-only is the same "collapse when space-constrained" move the
 * sidebar already makes at its own `md` breakpoint (icon rail, no
 * labels, before `lg` brings labels back). */
const BOTTOM_TAB_ITEMS = [
  { to: "/", label: "Home", end: true, Icon: HomeIcon },
  { to: "/customers", label: "Customers", Icon: CustomersIcon },
  { to: "/work-orders", label: "Work Orders", Icon: WorkOrderIcon },
  { to: "/invoices", label: "Invoices", Icon: InvoicesIcon },
  { to: "/technicians", label: "Technicians", Icon: TechniciansIcon },
];

const NAV_ACTIVE = "bg-[#0e2a47] text-white shadow-[inset_3px_0_0_#4fc0cf]";
const NAV_INACTIVE = "text-[#9fb6cf] hover:bg-[#0e2a47]/60 hover:text-white";

function SidebarNavLink({ to, label, end, Icon }: { to: string; label: string; end?: boolean; Icon: typeof HomeIcon }) {
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      className={({ isActive }) =>
        `flex min-h-11 items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium no-underline transition-colors ${
          isActive ? NAV_ACTIVE : NAV_INACTIVE
        }`
      }
    >
      <Icon className="flex-none" />
      <span className="hidden truncate lg:inline">{label}</span>
    </NavLink>
  );
}

/** Shared by the sidebar's account trigger and the bottom tab bar's —
 * same conditional links AppShell.tsx's dropdown shows (User Profile
 * always, Shop Settings for owners, Admin Panel for platform admins), plus
 * Log out folded in here too (the bottom tab bar has no separate pinned
 * logout slot the way the sidebar does — see the mockup's own 5-tab
 * limit). `onLogoutRequest` (not an owned `useConfirmGate()`/dialog here)
 * — the confirm dialog needs to survive this menu closing, since closing
 * it is exactly what unmounts this component; owning the dialog here used
 * to unmount the just-opened confirm prompt in the same tick "Log out"
 * was clicked (real bug: "Log out" appeared to do nothing). The caller
 * (AppShellModern) owns that gate/dialog at a level that's always
 * mounted. */
function SettingsMenu({
  onClose,
  align,
  onLogoutRequest,
}: {
  onClose: () => void;
  align: "sidebar" | "bottom";
  onLogoutRequest: () => void;
}) {
  const { session } = useAuth();

  const linkClass = "block rounded-lg px-3 py-2.5 text-sm font-medium text-[#071a33] no-underline hover:bg-[#f1f4f9]";

  return (
    <div
      role="menu"
      className={`absolute z-20 w-56 rounded-xl border border-[#e2e7ee] bg-white p-2 shadow-[0_20px_50px_rgba(0,0,0,.25)] ${
        align === "sidebar" ? "bottom-0 left-full ml-2" : "bottom-full right-2 mb-2"
      }`}
      onClick={onClose}
    >
      <NavLink to="/profile" className={linkClass}>
        User Profile
      </NavLink>
      {session?.role === "owner" && (
        <NavLink to="/settings" className={linkClass}>
          Shop Settings
        </NavLink>
      )}
      {session?.role === "owner" && (
        <NavLink to="/settings/predefined-items" className={linkClass}>
          Predefined Items
        </NavLink>
      )}
      {session?.role === "owner" && (
        <NavLink to="/support" className={linkClass}>
          Support
        </NavLink>
      )}
      {session?.user.isPlatformAdmin && (
        <NavLink to="/admin" className={linkClass}>
          Admin Panel
        </NavLink>
      )}
      <button
        type="button"
        className={`${linkClass} w-full text-left text-[#b3261e]`}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
          onLogoutRequest();
        }}
      >
        Log out
      </button>
    </div>
  );
}

export function AppShellModern({ children }: { children: ReactNode }) {
  const { session, logout } = useAuth();
  const shopType = useShopType();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The Inspections page only shows up for a shop that has set up a state-inspection item
  // (owner/staff on an auto-repair shop) — no separate setting to switch on.
  const canSeeInspections = (session?.role === "owner" || session?.role === "staff") && shopType !== "detailing";
  const inspectionItemsQuery = useQuery({
    queryKey: ["predefined-items", "has-inspection-item"],
    queryFn: () => predefinedItemsApi.list({ includeInactive: true, limit: 100 }),
    enabled: canSeeInspections,
    staleTime: 5 * 60 * 1000,
  });
  const showInspections = canSeeInspections && (inspectionItemsQuery.data?.items.some((i) => i.data.stateInspection === true) ?? false);
  const [contentZoom, setContentZoom] = useState<number>(readStoredContentZoom);
  // Owned HERE, not inside SettingsMenu — see that component's own
  // comment on why (closing the menu unmounts it, which used to unmount
  // this exact confirm dialog before the user could ever click "Yes, Log
  // Out").
  const logoutConfirm = useConfirmGate();

  // Only one of these two trigger+menu containers is ever visually present
  // at a time (sidebar at `md`+, bottom tab bar below it), but both exist
  // in the DOM simultaneously — checking against whichever one the open
  // menu actually lives in closes it on any outside click (e.g. clicking a
  // sidebar nav link, or anywhere else in the page) instead of only via
  // the trigger's own toggle.
  const sidebarSettingsRef = useRef<HTMLDivElement>(null);
  const bottomSettingsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!settingsOpen) return;
    function handleOutsideClick(e: MouseEvent): void {
      const target = e.target as Node;
      if (sidebarSettingsRef.current?.contains(target)) return;
      if (bottomSettingsRef.current?.contains(target)) return;
      setSettingsOpen(false);
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [settingsOpen]);

  function requestLogout(): void {
    logoutConfirm.request(() => {
      void logout().then(() => navigate("/login"));
    });
  }

  useEffect(() => {
    localStorage.setItem(CONTENT_ZOOM_STORAGE_KEY, String(contentZoom));
  }, [contentZoom]);

  // Same ["shop-profile"] query key/gating as AppShell.tsx — see that
  // file's own comments on why (SettingsPage's logo mutations write back
  // to this exact key, and a logo/name is never shown unless BOTH the
  // toggle is on and the value actually exists).
  const shopProfileQuery = useQuery({
    queryKey: ["shop-profile"],
    queryFn: shopProfileApi.get,
    enabled: !!session,
  });
  const logoVisible = !!(shopProfileQuery.data?.shopProfile.showLogoInHeader && shopProfileQuery.data.shopProfile.logoUrl);
  const logoUrl = logoVisible ? (shopProfileQuery.data?.shopProfile.logoUrl ?? null) : null;
  const shopNameVisible = logoVisible ? (shopProfileQuery.data?.shopProfile.showShopNameInHeader ?? true) : true;
  const tenantName = session?.tenantDisplayName ?? "AutoRepair";

  // Platform-admin-only notification badge (see the account trigger button
  // below) — a new website/app submission or a customer's email reply,
  // polled rather than pushed since there's no realtime channel in this
  // app. 45s balances "feels prompt" against not hammering the endpoint.
  const unreadSupportQuery = useQuery({
    queryKey: ["admin", "support-inquiries", "unread-count"],
    queryFn: adminApi.unreadSupportCount,
    enabled: !!session?.user.isPlatformAdmin,
    refetchInterval: 45_000,
  });
  const unreadSupportCount = unreadSupportQuery.data?.count ?? 0;

  useEffect(() => {
    if (session?.tenantDisplayName) document.title = `Bay2Bill - ${session.tenantDisplayName}`;
    return () => {
      document.title = "Bay2Bill";
    };
  }, [session?.tenantDisplayName]);

  function handleSearch(e: React.FormEvent): void {
    e.preventDefault();
    const q = query.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f6f8fb]">
      {/* Its own full-width row above the sidebar/content row below —
          placing it INSIDE the `md:flex-row` container (as a sibling of
          <aside> and the content column) made it just another column at
          desktop widths instead of a banner spanning the top, which is
          exactly the "left 1/3 of the screen" bug this fixes. */}
      <DevEnvironmentBanner />

      <div className="flex flex-1 flex-col md:flex-row">
        {/* Sidebar — hidden below `md` (bottom tab bar takes over instead),
            icon-only rail from `md` to `lg`, full width with labels at `lg`+. */}
        {/* `h-screen self-start` (not `h-full`, which needs a
            percentage-height ancestor chain that wasn't reliably there —
            measured ~482px tall on a 900px viewport; and not `min-h-screen`
            either, which fixed THAT but then let this stretch to match the
            main content column's height on a page longer than one screen —
            reported live: Settings/Logout, pinned via `mt-auto` inside this
            box, ended up positioned partway down a long page instead of at
            the actual viewport bottom, since they're `mt-auto` WITHIN
            whatever height this box ends up being). `h-screen` is a fixed,
            absolute 100vh — `self-start` overrides this row's default
            `align-items: stretch`, which would otherwise still re-stretch
            an explicitly-sized item to match its tallest sibling. Together:
            always exactly one viewport tall, never more, never less,
            regardless of how long the main content column is — matching
            the owner's own ask ("stretch to the viewport... while right
            panel can be of any length"). */}
        <aside className="sticky top-0 hidden h-screen flex-none flex-col gap-4 self-start bg-[#071a33] pb-4 md:flex md:w-[76px] lg:w-60">
        {/* `h-[76px]` matches the header's own height exactly (see the
            `<header>` below, given the same explicit height for the same
            reason) — the owner asked for the logo to "stretch from top to
            the same line dividing the search bar and content," i.e. this
            band's bottom edge lines up with the header's bottom border.
            The `<img>` fills that full height (`h-full`) with `w-auto` and
            `object-contain`, so width scales proportionally with whatever
            height is available instead of being capped at a fixed rem
            value that would leave it looking small — never distorts the
            logo's own aspect ratio either way. */}
        <div className="flex h-[76px] w-full items-center justify-center bg-white px-3">
          {logoUrl ? (
            <img src={withBase(logoUrl)} alt="" className="h-full max-h-full w-auto max-w-full object-contain" />
          ) : (
            <span className="truncate text-sm font-bold text-[#071a33]">{shopNameVisible ? tenantName : "Bay2Bill"}</span>
          )}
        </div>
        {shopNameVisible && logoUrl && (
          <div className="hidden px-4 lg:block">
            <span className="block truncate text-sm font-bold text-white">{tenantName}</span>
          </div>
        )}

        <nav className="flex flex-col gap-1 px-2.5">
          {NAV_ITEMS.map((item) => (
            <SidebarNavLink key={item.to} {...item} />
          ))}
          {/* Owner-only (same session.role gate SettingsMenu already uses
              below) — can't live in the static NAV_ITEMS array since that's
              module-level with no access to the session. Matches the
              mockup's own nav order: last in the top group, right after
              Technicians. */}
          {session?.role === "owner" && (
            <SidebarNavLink to="/reports/payments-received" label="Payments" Icon={PaymentsIcon} />
          )}
          {showInspections && <SidebarNavLink to="/inspections" label="Inspections" Icon={InspectionsIcon} />}
        </nav>

        <div ref={sidebarSettingsRef} className="relative mt-auto flex flex-col gap-1 px-2.5">
          {/* Combined account trigger (avatar initials + full name) —
              opens the same User Profile/Shop Settings/Admin Panel/Log out
              menu a separate gear-icon "Settings" button used to, folded
              in here per the owner's ask instead of keeping that button
              and a second, always-visible pinned logout row as two
              separate controls. Avatar badge markup/colors unchanged from
              that old pinned row's own. */}
          <button
            type="button"
            title={unreadSupportCount > 0 ? `Account & settings — ${unreadSupportCount} unread support message${unreadSupportCount === 1 ? "" : "s"}` : "Account & settings"}
            aria-haspopup="menu"
            aria-expanded={settingsOpen}
            onClick={() => setSettingsOpen((v) => !v)}
            className={`flex min-h-11 items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-sm font-medium ${NAV_INACTIVE}`}
          >
            <span className="relative flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full bg-[#12324f] text-[10px] font-semibold text-[#9fd8e6]">
              {initialsOf(session?.user.fullName)}
              {unreadSupportCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#071a33] bg-[#e0563f]" aria-hidden="true" />
              )}
            </span>
            <span className="hidden min-w-0 flex-1 items-center gap-2 lg:flex">
              <span className="truncate">{session?.user.fullName ?? "Account"}</span>
              {unreadSupportCount > 0 && (
                <span className="flex-none rounded-full bg-[#e0563f] px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                  {unreadSupportCount}
                </span>
              )}
            </span>
          </button>
          {settingsOpen && <SettingsMenu align="sidebar" onClose={() => setSettingsOpen(false)} onLogoutRequest={requestLogout} />}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* `min-h-[76px]` (not just letting content/padding determine
            height) so this lines up exactly with the sidebar's own logo
            band at `md`+ (where the sidebar — and so this alignment —
            actually exists; below `md` the bottom tab bar takes over
            instead, see the sidebar's own `hidden md:flex`). `min-h`
            rather than a hard `h` so this can still grow if the search
            field/button ever wrap to two lines on an unusually narrow
            width, instead of clipping them. */}
        <header className="flex min-h-[76px] flex-wrap items-center justify-between gap-3 border-b border-[#e2e7ee] bg-white px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2 md:hidden">
            {logoUrl && <img src={withBase(logoUrl)} alt="" className="h-6 w-auto object-contain" />}
            {shopNameVisible && <span className="max-w-[9rem] truncate text-sm font-bold text-[#071a33]">{tenantName}</span>}
          </div>

          <form
            onSubmit={handleSearch}
            className="flex h-11 min-w-[10rem] flex-1 items-center gap-2 rounded-[10px] border border-[#e2e7ee] bg-[#f1f4f9] px-3 sm:max-w-[420px]"
          >
            <SearchIcon className="flex-none text-[#6b7a8f]" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={shopType === "detailing" ? "Search customers, invoices…" : "Search customers, vehicles, invoices…"}
              aria-label="Search"
              className="w-full truncate border-0 bg-transparent text-sm text-[#101b2d] outline-none placeholder:text-[#8b97a8]"
            />
          </form>

          {/* Grouped in one flex-none wrapper (not two separate header
              children) so the zoom controls always wrap/sit immediately
              next to "New Work Order" as a single unit, instead of
              `justify-between` being free to drift them apart. */}
          <div className="flex flex-none items-center gap-2">
            <div className="flex flex-none items-center gap-1" role="group" aria-label="Adjust content zoom">
              <button
                type="button"
                title="Decrease content zoom"
                aria-label="Decrease content zoom"
                onClick={() => setContentZoom((z) => Math.max(CONTENT_ZOOM_MIN, z - CONTENT_ZOOM_STEP))}
                disabled={contentZoom <= CONTENT_ZOOM_MIN}
                className="flex h-11 w-11 flex-none items-center justify-center rounded-[10px] border border-[#e2e7ee] text-[#4a5361] hover:bg-[#f1f4f9] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ZoomOutIcon />
              </button>
              <button
                type="button"
                title="Reset content zoom to 100%"
                aria-label="Reset content zoom to 100%"
                onClick={() => setContentZoom(100)}
                disabled={contentZoom === 100}
                className="flex h-11 flex-none flex-col items-center justify-center gap-0 rounded-[10px] border border-[#e2e7ee] px-3 leading-none text-[#8b97a8] hover:bg-[#f1f4f9] disabled:cursor-not-allowed"
              >
                <span className="text-[11px] font-semibold tabular-nums">{contentZoom}%</span>
                <span className="mt-0.5 text-[13px] font-bold">Reset Zoom</span>
              </button>
              <button
                type="button"
                title="Increase content zoom"
                aria-label="Increase content zoom"
                onClick={() => setContentZoom((z) => Math.min(CONTENT_ZOOM_MAX, z + CONTENT_ZOOM_STEP))}
                disabled={contentZoom >= CONTENT_ZOOM_MAX}
                className="flex h-11 w-11 flex-none items-center justify-center rounded-[10px] border border-[#e2e7ee] text-[#4a5361] hover:bg-[#f1f4f9] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ZoomInIcon />
              </button>
            </div>

            <a
              href="/work-orders/new"
              onClick={(e) => {
                e.preventDefault();
                navigate("/work-orders/new");
              }}
              className={modernButtonClassName("primary", "flex-none no-underline")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
                <path d="M12 5v14M5 12h14" />
              </svg>
              New Work Order
            </a>
          </div>
        </header>

        <main className="flex-1 px-4 pb-24 pt-6 sm:px-5 md:pb-8" style={{ zoom: `${contentZoom}%` }}>
          {children}
        </main>
        <div className="hidden md:block">
          <ModernFooter />
        </div>
        <LegalAcceptanceGate />
      </div>
      </div>

      {/* Bottom tab bar — phone width only. Icon-only (see
          BOTTOM_TAB_ITEMS's own comment on why) — each item still carries
          its label via `title` (a tooltip on long-press/hover) and
          `aria-label`, so nothing is lost for assistive tech even though
          nothing is shown visually. */}
      <nav className="fixed inset-x-0 bottom-0 z-10 flex border-t border-[#0d2947] bg-[#071a33] px-1 py-1 md:hidden">
        {BOTTOM_TAB_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            title={item.label}
            aria-label={item.label}
            className={({ isActive }) =>
              `flex min-h-12 flex-1 items-center justify-center no-underline ${isActive ? "text-[#7fd3e2]" : "text-[#93a9c2]"}`
            }
          >
            <item.Icon />
          </NavLink>
        ))}
        {session?.role === "owner" && (
          <NavLink
            to="/reports/payments-received"
            title="Payments"
            aria-label="Payments"
            className={({ isActive }) =>
              `flex min-h-12 flex-1 items-center justify-center no-underline ${isActive ? "text-[#7fd3e2]" : "text-[#93a9c2]"}`
            }
          >
            <PaymentsIcon />
          </NavLink>
        )}
        {showInspections && (
          <NavLink
            to="/inspections"
            title="Inspections"
            aria-label="Inspections"
            className={({ isActive }) =>
              `flex min-h-12 flex-1 items-center justify-center no-underline ${isActive ? "text-[#7fd3e2]" : "text-[#93a9c2]"}`
            }
          >
            <InspectionsIcon />
          </NavLink>
        )}
        {/* A <button> can't validly contain another <button> — SettingsMenu
            renders its own (e.g. "Log out"), so it must be a SIBLING of the
            trigger, not a child of it, matching the sidebar's own version
            of this same menu just above. The old nested version threw a
            real React/DOM-nesting warning and browsers "fix" invalid
            nesting by hoisting the inner element out of the outer one,
            which is exactly the kind of undefined, browser-specific
            behavior this restructure avoids relying on. */}
        <div ref={bottomSettingsRef} className="relative flex min-h-12 flex-1 items-center justify-center">
          <button
            type="button"
            title={unreadSupportCount > 0 ? `Account & settings — ${unreadSupportCount} unread support message${unreadSupportCount === 1 ? "" : "s"}` : "Account & settings"}
            aria-label="Account & settings"
            aria-haspopup="menu"
            aria-expanded={settingsOpen}
            onClick={() => setSettingsOpen((v) => !v)}
            className="flex items-center justify-center"
          >
            <span
              className={`relative flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full text-[10px] font-semibold ${
                settingsOpen ? "bg-[#7fd3e2] text-[#071a33]" : "bg-[#12324f] text-[#9fd8e6]"
              }`}
            >
              {initialsOf(session?.user.fullName)}
              {unreadSupportCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#071a33] bg-[#e0563f]" aria-hidden="true" />
              )}
            </span>
          </button>
          {settingsOpen && <SettingsMenu align="bottom" onClose={() => setSettingsOpen(false)} onLogoutRequest={requestLogout} />}
        </div>
      </nav>

      <ModernConfirmDialog {...logoutConfirm.dialogProps} message="Log out?" confirmLabel="Yes, Log Out" />
    </div>
  );
}

/** First letter of up to the first two whitespace-separated words in a
 * full name, uppercased — same rule the old pinned logout row used. */
function initialsOf(fullName: string | undefined): string {
  return (
    fullName
      ?.split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?"
  );
}
